import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DataTable, EmptyState, FormGrid, Pagination, Pill, SelectField, TextField, type Column } from "@atlas/ui/web";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { atlas } from "../../lib/sdk";
import type { AdminAuditEntry, AuditCategoria } from "@atlas/sdk";
import { rotuloSubOrganizacao } from "../../lib/registro-agente";

const VAR_BY_CAT: Record<AuditCategoria, "averbado" | "emdia" | "pendente" | "rejeitada" | "expirado" | "aceita"> = {
  pre_reserva: "pendente",
  termo_aceite: "aceita",
  biometria: "averbado",
  dados_pessoais: "emdia",
  margem: "expirado",
  tombamento: "averbado",
  id_unico: "emdia",
  convenio_config: "emdia",
  acesso: "aceita",
};

/** Regras de aplicabilidade das colunas CPF/Matricula/ID unico por evento.
 *  Retorna false quando o campo NAO faz sentido pra aquele evento (vira "n/a"
 *  na UI, cinza dim); true quando faz sentido mas veio vazio → "—" (sinaliza
 *  bug de instrumentacao). Heuristica corrigida em 22/07/2026:
 *
 *  Antes: lista de acoes/categorias hardcoded — deu falsos negativos com
 *  acoes admin novas (folha_consolidada, adf_aplicada_admin, etc) que caem
 *  em categoria=margem mas nao referenciam servidor. Resultado: "—" onde
 *  devia ser "n/a".
 *
 *  Agora: PRESENCA de cpf/matricula no entry + relacao a um servidor
 *  especifico. Se o entry veio SEM cpf, ele so seria "esperado" quando o
 *  userRole indica que o ator e o proprio servidor OU quando a acao esta na
 *  lista pequena de eventos que sabidamente carregam cpf mesmo do lado
 *  admin (pre_reserva_cancelada). Fora disso, ausencia = n/a. */
function isServidorCentric(e: AdminAuditEntry): boolean {
  if (e.userRole === "servidor") return true;
  const acoesServidorCentric = new Set([
    "pre_reserva_criada", "pre_reserva_cancelada",
    "termo_confirmado_na_proposta",
    "primeiro_acesso_conclusao", "senha_redefinida",
    "servidor_editado", "base_importada_matricula",
    "adf_aprovada_prefeitura", "adf_negada_prefeitura",
  ]);
  return acoesServidorCentric.has(e.acao);
}
function cpfAplicavel(e: AdminAuditEntry): boolean {
  if (e.cpf) return true;
  return isServidorCentric(e);
}
function matriculaAplicavel(e: AdminAuditEntry): boolean {
  if (e.matricula) return true;
  return isServidorCentric(e);
}
function idUnicoAplicavel(e: AdminAuditEntry): boolean {
  // ID Unico so aparece em eventos da categoria id_unico ou pre_reserva
  // cancelada (que carrega o ID da reserva). No resto — nao aplicavel.
  if (e.categoria === "id_unico") return true;
  if (e.acao === "pre_reserva_cancelada") return true;
  return false;
}
function renderCampo(valor: string | undefined, aplicavel: boolean) {
  if (valor) return valor;
  if (!aplicavel) return <span style={{ color: "var(--text-dim)", fontStyle: "italic" }}>n/a</span>;
  return "—";
}

export function AdminAuditoria() {
  const [categoria, setCategoria] = useState<"" | AuditCategoria>("");
  // Recorte no CLIENTE. O `limit` que a rota aceita e uma JANELA ("os N mais
  // recentes"), nao uma pagina: `listAuditAsync` nao conhece `offset`. Paginar
  // dentro de uma janela ja truncada deixaria o operador virar paginas achando
  // que viu tudo. Aqui pagina-se a janela que chegou, e isso e honesto.
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [cpf, setCpf] = useState("");
  const [matricula, setMatricula] = useState("");
  const [propostaId, setPropostaId] = useState("");
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");
  /** Sub organizacao de quem agiu pelo banco: parte do nome, ou "principal". */
  const [subOrganizacao, setSubOrganizacao] = useState("");

  // `offset` presente troca o modo da rota: ela deixa de devolver uma JANELA
  // ("os 300 mais recentes") e passa a devolver uma PAGINA de verdade, lida
  // pura do Postgres, com o total do conjunto filtrado.
  //
  // A tela pedia `limit: 300` e fatiava no cliente — ou seja, o operador
  // virava paginas dentro de uma janela truncada, acreditando ter visto a
  // trilha inteira quando via os 300 ultimos. Ver 007/D4.
  const data = useQuery({
    queryKey: ["admin", "auditoria", categoria, cpf, matricula, propostaId, subOrganizacao, desde, ate, pagina, porPagina],
    queryFn: () =>
      atlas.admin.listAuditoria({
        categoria: categoria || undefined,
        cpf: cpf || undefined,
        matricula: matricula || undefined,
        proposta_id: propostaId || undefined,
        subOrganizacao: subOrganizacao.trim() || undefined,
        desde: desde ? new Date(desde).toISOString() : undefined,
        ate: ate ? new Date(ate).toISOString() : undefined,
        limit: porPagina,
        offset: (pagina - 1) * porPagina,
      }),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });
  // Trocar filtro com a posicao antiga cairia num offset que ja nao existe.
  useEffect(() => { setPagina(1); }, [categoria, cpf, matricula, propostaId, subOrganizacao, desde, ate]);
  const daPagina = recorteDoServidor(data.data?.entries ?? [], data.data?.total ?? 0, pagina, porPagina);
  // Serve so' ao `emptyState`, para nao afirmar "trilha vazia" quando o que
  // esta vazio e' o recorte.
  //
  // AINDA NAO E' `useFiltros`, e a razao e' de sequencia: o hook entrega
  // contador e reset, e reset precisa de um lugar para morar. Esta tela nao
  // usa `FilterBar` — os seis campos ficam num `FormGrid` solto —, entao pendurar
  // o botao aqui seria montar a barra, que e' justamente o trabalho do grupo da
  // Auditoria (identificador tipado + presets de periodo). Ate la a tela segue
  // sem reset, e por isso a mensagem de vazio fala em limpar os CAMPOS: nao
  // promete um botao que nao existe.
  const temFiltro = !!(categoria || cpf || matricula || propostaId || subOrganizacao || desde || ate);

  const columns: Column<AdminAuditEntry>[] = [
    { key: "ts", header: "Quando", render: (e) => new Date(e.ts).toLocaleString("pt-BR") },
    { key: "categoria", header: "Categoria", render: (e) => <Pill variant={VAR_BY_CAT[e.categoria] ?? "emdia"}>{e.categoria}</Pill> },
    { key: "acao", header: "Ação", mono: true },
    { key: "cpf", header: "CPF", render: (e) => renderCampo(e.cpf, cpfAplicavel(e)) },
    { key: "matricula", header: "Matrícula", render: (e) => renderCampo(e.matricula, matriculaAplicavel(e)) },
    { key: "idUnico", header: "ID único", mono: true, render: (e) => renderCampo(e.idUnico, idUnicoAplicavel(e)) },
    // Com `agente` (acao do banco desde 01/10/2026) mostra o NOME de quem agiu;
    // o id fica no title. Entradas anteriores seguem mostrando so' o id.
    {
      key: "user",
      header: "Usuário",
      render: (e) =>
        e.agente ? <span title={e.userId}>{e.agente.usuarioNome}</span> : e.userId ? `${e.userId}` : "—",
    },
    // So' faz sentido para acao do banco: as demais mostram "—", nao
    // "Organização principal".
    { key: "subOrganizacao", header: "Sub organização", render: (e) => (e.agente ? rotuloSubOrganizacao(e.agente) : "—") },
    { key: "detalhes", header: "Detalhes", render: (e) => e.detalhes },
    { key: "trace_id", header: "Trace", mono: true },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Auditoria</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 780 }}>
          Trilha de auditoria append-only: pré-reservas, aceites de termo, alterações de dados pessoais, movimentações de margem, tombamento, config de convênio, ID único e acessos ao painel.
          Cada registro guarda timestamp, IP/device quando aplicável, CPF mascarado, ID da proposta e termo aceito. Campos marcados como <em style={{ color: "var(--text-dim)" }}>n/a</em> não se aplicam ao tipo do evento (ex.: um login por e-mail não carrega CPF).
        </p>
      </header>

      <div style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: 20 }}>
        <FormGrid cols={3}>
          <SelectField
            label="Categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as "" | AuditCategoria)}
            options={[
              { value: "", label: "Todas" },
              ...(data.data?.categorias ?? []).map((c) => ({ value: c.value, label: c.label })),
            ]}
          />
          <TextField label="CPF mascarado" value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="ex: 000.***.***-33" />
          <TextField label="Matrícula" value={matricula} onChange={(e) => setMatricula(e.target.value)} placeholder="ex: M-9001" />
          <TextField label="ID da proposta" value={propostaId} onChange={(e) => setPropostaId(e.target.value)} />
          <TextField
            label="Sub organização"
            value={subOrganizacao}
            onChange={(e) => setSubOrganizacao(e.target.value)}
            placeholder="nome, ou principal"
            hint="Ações da consignatária. Digite principal para as da organização principal."
          />
          <TextField label="Desde" type="datetime-local" value={desde} onChange={(e) => setDesde(e.target.value)} />
          <TextField label="Até" type="datetime-local" value={ate} onChange={(e) => setAte(e.target.value)} />
        </FormGrid>
      </div>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(e) => e.id}
        loading={data.isLoading}
        // Nao havia `emptyState` NENHUM: caia no default da biblioteca,
        // "Nenhum item encontrado.". Numa tela com seis filtros — incluindo um
        // intervalo de data/hora — isso torna um periodo estreito demais
        // indistinguivel de uma trilha genuinamente vazia. Numa TRILHA DE
        // AUDITORIA essa e' a confusao mais cara do repo: concluir que nao houve
        // evento e' uma afirmacao sobre o passado, e o operador nao tem como
        // saber que ela veio de um filtro.
        //
        // Falha de request tambem nao pode virar vazio (FR-013): sem este ramo
        // um erro de rede diria "nenhum evento", e o `EmptyState` nao tem
        // variante de erro de proposito.
        emptyState={
          data.isError
            ? "Não foi possível carregar a trilha de auditoria. Tente novamente."
            : <EmptyState
                motivo={temFiltro ? "filtro-vazio" : "sem-dados"}
                filtroVazio="Nenhum evento bate com esses filtros. Amplie o período ou limpe os campos para ver a trilha."
                semDados="Nenhum evento registrado na trilha ainda."
              />
        }
      />

      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="eventos"
      />
    </div>
  );
}
